/**
 * What the agent can do beyond talking, with no change to its image: it appends a fenced block to its reply, the
 * browser removes the block from the text and acts on it. The same approach as Coplay's chat cards.
 */
import { z } from 'zod'
import { validateView } from './ui/spec/validate'
import type { CogentView } from './ui/view/View'

export const COGENT_FENCE = 'softmax-cogent'

const NOTE_MAX = 200
const REMEMBER_MAX = 200

export const pageAction = z.object({
  kind: z.enum(['point', 'open', 'press', 'go']),
  ref: z
    .string()
    .regex(/^(@\S+|e[0-9]+)$/)
    .max(200),
  note: z.string().min(1).max(NOTE_MAX),
})
export type PageAction = z.infer<typeof pageAction>

const block = z.object({
  actions: z.array(pageAction).max(8).default([]),
  views: z.array(z.unknown()).max(4).default([]),
  remember: z.array(z.string().min(1).max(REMEMBER_MAX)).max(4).default([]),
})

export type ParsedReply = {
  /** What is left to show as the agent's words. */
  text: string
  actions: PageAction[]
  views: CogentView[]
  remember: string[]
  /** Blocks the agent wrote that could not be used. Never shown as text; the agent is told next turn. */
  problems: string[]
}

const CLOSED = new RegExp(`^ {0,3}(\`{3,})${COGENT_FENCE}[ \\t]*\\r?\\n([\\s\\S]*?)^ {0,3}\\1[ \\t]*$`, 'gm')
/** A block still streaming in: hidden until it closes, so its JSON never flashes in the chat. */
const OPEN = new RegExp(`^ {0,3}\`{3,}${COGENT_FENCE}[\\s\\S]*$`, 'm')

export function parseReply(raw: string): ParsedReply {
  const parsed: ParsedReply = {
    text: '',
    actions: [],
    views: [],
    remember: [],
    problems: [],
  }

  const rest = raw.replace(CLOSED, (_whole, _fence, payload: string) => {
    let json: unknown

    try {
      json = JSON.parse(payload)
    } catch {
      parsed.problems.push('A block was not valid JSON.')
      return ''
    }

    const result = block.safeParse(json)

    if (!result.success) {
      parsed.problems.push(z.prettifyError(result.error).slice(0, 400))
      return ''
    }

    parsed.actions.push(...result.data.actions)
    for (const view of result.data.views) {
      const issues = validateView(view)

      if (issues.length)
        parsed.problems.push(
          `A view was not shown: ${issues
            .slice(0, 5)
            .map(({ path, message }) => `${path} ${message}`.trim())
            .join('; ')}`.slice(0, 800)
        )
      else parsed.views.push(view as CogentView)
    }
    parsed.remember.push(...result.data.remember)
    return ''
  })

  parsed.text = rest.replace(OPEN, '').trim()
  return parsed
}
